import React, { useContext, useState } from 'react';
import { UserContext } from '../../lib/usercontext';
import MasterLayout from '../layouts/masterlayout';
import CustomerLayout from '../layouts/customerlayout';
import ClientLayout from '../layouts/clientlayout';
import InfoCard from '../common/info_card';
import { TruckIcon, UserGroupIcon, BookOpenIcon, CalendarIcon } from '@heroicons/react/24/outline';
import ProductionReportModal from '../common/modals/production_report_modal';
import ReportModal from '../common/modals/report_modal';
import BoxLabelModal from '../common/modals/box_label_modal';

function Reports() {
  const { user } = useContext(UserContext);
  const role = user.userGroups ? user.userGroups[0] : 'Customer';
  const pageTitle = 'Reports';

  const [productionModalOpen, setProductionModalOpen] = useState(false);
  const [activeReport, setActiveReport] = useState(null);

  // Admin Group 1
  const adminGroupOne = (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-4">
      <InfoCard
        Icon={TruckIcon}
        title="Item Production Report"
        description="This will create a full production report for the selected date range."
        linkText="Create Report"
        linkHref="#"
        method={() => setProductionModalOpen(true)}
      />
      {productionModalOpen && (
        <ProductionReportModal
          isOpen={productionModalOpen}
          onClose={() => setProductionModalOpen(false)}
        />
      )}
      <InfoCard
        Icon={CalendarIcon}
        title="Packing Slips"
        description="Generate packing slips for the selected delivery."
        linkText="Create Report"
        method={() => setActiveReport('delivery_ticket')}
      />
      {activeReport === 'delivery_ticket' && (
        <ReportModal
          isOpen={activeReport === 'delivery_ticket'}
          onClose={() => setActiveReport(null)}
          title="Packing Slips"
          description="Generate packing slip"
          endpoint="/reports/delivery_ticket_report/pdf"
          filenamePrefix="delivery_ticket_report"
          downloadOption="open"
        />
      )}
      <InfoCard
        Icon={CalendarIcon}
        title="Client Total Sheet Report"
        description="Total entrees per provider per date, grouped by menu"
        linkText="Create Report"
        method={() => setActiveReport('menu_date_provider')}
      />
      {activeReport === 'menu_date_provider' && (
        <ReportModal
          isOpen={activeReport === 'menu_date_provider'}
          onClose={() => setActiveReport(null)}
          title="Client Total Sheet Report"
          description="Generate report showing entrees per provider per date grouped by menu, followed by client totals."
          endpoint="/reports/providers_menu_date/pdf"
          filenamePrefix="menu_date_provider_report"
          downloadOption="open"
          dateMode="single"
          filtersConfig={{ date: true, deliveryStatus: true, client: true, menu: false }}
        />
      )}
    </div>
  );

  // Admin Group 2
  const adminGroupTwo = (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-4">
      <InfoCard
        Icon={UserGroupIcon}
        title="Provider Item Report"
        description="This will generate a packout report for all providers or single provider showing all items."
        linkText="Create Report"
        method={() => setActiveReport('provider_items')}
      />
      {activeReport === 'provider_items' && (
        <ReportModal
          isOpen={activeReport === 'provider_items'}
          onClose={() => setActiveReport(null)}
          title="Provider Item Report"
          description="Generate detailed item-level report"
          endpoint="/reports/production_providers_items_types/pdf"
          filenamePrefix="provider_item_report"
          downloadOption="open"
        />
      )}
      <InfoCard
        Icon={UserGroupIcon}
        title="Provider Type Report"
        description="Report grouped by provider and item types with type totals"
        linkText="Create Report"
        method={() => setActiveReport('provider_type')}
      />
      {activeReport === 'provider_type' && (
        <ReportModal
          isOpen={activeReport === 'provider_type'}
          onClose={() => setActiveReport(null)}
          title="Provider Type Report"
          description="Generate report grouped by provider and item types"
          endpoint="/reports/production_providers_types/pdf"
          filenamePrefix="provider_type_report"
          downloadOption="open"
        />
      )}
      <InfoCard
        Icon={BookOpenIcon}
        title="Provider Total Report"
        description="Report grouped by provider and menu's with entree totals"
        linkText="Create Report"
        method={() => setActiveReport('provider_total')}
      />
      {activeReport === 'provider_total' && (
        <ReportModal
          isOpen={activeReport === 'provider_total'}
          onClose={() => setActiveReport(null)}
          title="Provider Total Report"
          description="Generate grouped by provider and menu's with entree totals"
          endpoint="/reports/providers_menu_total/pdf"
          filenamePrefix="provider_total_report"
          downloadOption="open"
        />
      )}
      <InfoCard
        Icon={BookOpenIcon}
        title="Billing Summary Report"
        description="Generate billing summary for the selected date range."
        linkText="Create Report"
        method={() => setActiveReport('billing_summary_admin')}
      />
      {activeReport === 'billing_summary_admin' && (
        <ReportModal
          isOpen={activeReport === 'billing_summary_admin'}
          onClose={() => setActiveReport(null)}
          title="Billing Summary Report"
          description="Generate billing summary report"
          endpoint="/reports/billing_summary/pdf"
          filenamePrefix="billing_summary_report"
          downloadOption="open"
          dateMode="range"
        />
      )}
      {/* Print Packing Slip / Delivery Ticket */}
      <InfoCard
        Icon={TruckIcon}
        title="Print Delivery Ticket"
        description="Generate delivery tickets for your location on a given date."
        linkText="Create Report"
        method={() => setActiveReport('packing_slips')}
      />
      {activeReport === 'packing_slips' && (
        <ReportModal
          isOpen={activeReport === 'packing_slips'}
          onClose={() => setActiveReport(null)}
          title="Print Delivery Ticket"
          description="Generate delivery ticket"
          endpoint="/reports/packing_slips/pdf"
          filenamePrefix="packing_slips_report"
          downloadOption="open"
          dateMode="single"
        />
      )}
      {/* Box Labels */}
      <InfoCard
        Icon={TruckIcon}
        title="Print Box Labels"
        description="Generate box labels for the selected route and dates."
        linkText="Create Report"
        method={() => setActiveReport('box_labels')}
      />
      {activeReport === 'box_labels' && (
        <BoxLabelModal
          isOpen={activeReport === 'box_labels'}
          onClose={() => setActiveReport(null)}
        />
      )}
    </div>    
  );

  const adminCards = (
    <>
      {adminGroupOne}
      <hr className="my-6" />
      {adminGroupTwo}
    </>
  );

  const customerCards = <></>;
  const providerCards = <></>;

  // Provider Cards
  const clientCards = (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-4">
      {/* Delivery Ticket */}
      <InfoCard
        Icon={CalendarIcon}
        title="Packing Slips"
        description="Generate packing slips for the selected delivery."
        linkText="Create Report"
        method={() => setActiveReport('delivery_ticket')}
      />
      {activeReport === 'delivery_ticket' && (
        <ReportModal
          isOpen={activeReport === 'delivery_ticket'}
          onClose={() => setActiveReport(null)}
          title="Packing Slips"
          description="Generate packing slip"
          endpoint="/reports/delivery_ticket_report/pdf"
          filenamePrefix="delivery_ticket_report"
          downloadOption="open"
        />
      )}
      {/* Client Total Sheet */}
      <InfoCard
        Icon={CalendarIcon}
        title="Client Total Sheet Report"
        description="Total entrees per provider per date, grouped by menu"
        linkText="Create Report"
        method={() => setActiveReport('menu_date_provider')}
      />
      {activeReport === 'menu_date_provider' && (
        <ReportModal
          isOpen={activeReport === 'menu_date_provider'}
          onClose={() => setActiveReport(null)}
          title="Client Total Sheet Report"
          description="Generate report showing entrees per provider per date grouped by menu, followed by client totals."
          endpoint="/reports/providers_menu_date/pdf"
          filenamePrefix="menu_date_provider_report"
          downloadOption="open"
          dateMode="single"
          filtersConfig={{ date: true, deliveryStatus: true, client: true, menu: false }}
        />
      )}
      {/* Billing Summary */}
      <InfoCard
        Icon={BookOpenIcon}
        title="Billing Summary Report"
        description="Generate billing summary for your location on a given date."
        linkText="Create Report"
        method={() => setActiveReport('billing_summary')}
      />
      {activeReport === 'billing_summary' && (
        <ReportModal
          isOpen={activeReport === 'billing_summary'}
          onClose={() => setActiveReport(null)}
          title="Billing Summary Report"
          description="Generate billing summary report"
          endpoint="/reports/billing_summary/pdf"
          filenamePrefix="billing_summary_report"
          downloadOption="open"
          dateMode="single"
        />
      )}
      
    </div>
  );

  return (
    <div>
      {role === 'Admin' ? (
        <MasterLayout pageTitle={pageTitle}>
          <div className="flex flex-col p-4">
            {adminCards}
          </div>
        </MasterLayout>
      ) : role === 'Provider' ? (
        <ClientLayout pageTitle={pageTitle}>
          <div className="flex flex-col p-4">{providerCards}</div>
        </ClientLayout>
      ) : role === 'Customer' ? (
        <CustomerLayout pageTitle={pageTitle}>
          <div className="flex flex-wrap items-start gap-x-4 gap-y-4 p-4">
            {customerCards}
          </div>
        </CustomerLayout>
      ) : (
        <ClientLayout pageTitle={pageTitle}>
          <div className="flex flex-wrap items-start gap-x-4 gap-y-4 p-4">
            {clientCards}
          </div>
        </ClientLayout>
      )}
    </div>
  );
}

export default Reports;
